// "Theme" (legacy window/themes.js): ten theme tiles (background preview, four sample cards, the back,
// the name, a "restore theme" link when the deck/back differ from the theme's defaults) and an
// Advanced tab (shown when the window is at least 1024 px wide, re-evaluated live) with pagers
// through the decks and the backs. Every click applies and saves at once.
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { t } from '../i18n';
import { backSrc, backSrcSet, backgroundSrc, faceSrc, faceSrcSet, previewSrc, TILE_SET_NAMES, tileDeckOf, tileSetOf, tileSrc, tileSrcSet } from '../options/assets';
import type { SkinValues } from '../options/storage';
import { uiStore, useUiState, type WindowInstance } from '../store/uiStore';
import { Tabs, WindowFrame } from './WindowFrame';
import { onMediaChange } from '../ui/compat';
import { BALL_SET_NAMES, ballSetOf, ballSrc } from '../kulki/balls';

interface Theme {
  name: string;
  id: string;
  deck: string;
  back: string;
  background: string;
}

// (no theme has an author line any more: the Modern theme's "theme made by @johnkappa" footer went on 2026-10-31, owner)
export const THEMES: Theme[] = [
  { name: 'Retro', id: 'classic_old', deck: 'classic_old', back: 'classic_old', background: 'classic_old' },
  { name: 'Modern', id: 'modern', deck: 'modern', back: 'modern', background: 'modern' },
  { name: 'Saloon', id: 'classic_saloon', deck: 'classic_old', back: 'classic_old', background: 'classic_saloon' },
  { name: 'Classic green', id: 'classic_green', deck: 'classic', back: 'classic_blue', background: 'classic_green' },
  { name: 'Strips blue', id: 'strips_blue', deck: 'strips', back: 'strips_blue', background: 'strips_blue' },
  { name: 'Classic red', id: 'classic_red', deck: 'classic', back: 'classic_red', background: 'classic_red' },
  { name: 'Pattern blue', id: 'pattern_blue', deck: 'classic', back: 'classic_blue', background: 'pattern_blue' },
  { name: 'Pattern green', id: 'pattern_green', deck: 'classic', back: 'classic_green', background: 'pattern_green' },
  { name: 'Honeycomb', id: 'honeycomb', deck: 'honeycomb', back: 'honeycomb', background: 'honeycomb' },
  { name: 'Pattern dark', id: 'pattern_dark', deck: 'animals', back: 'animals', background: 'pattern_dark' },
];
export const DECKS = ['classic_old', 'modern', 'classic', 'strips', 'honeycomb', 'animals'];
export const BACKS = ['classic_old', 'modern', 'classic_blue', 'classic_red', 'classic_green', 'strips_blue', 'honeycomb', 'animals'];
const TILE_CARDS = ['Ks', 'Qh', 'Jd', '10c'];
/** Three Mahjong tiles of the theme's set beside the cards (owner 2026-10-31: the deck option picks the tile set too, so
 *  the tile shows what Mahjong will look like); the flat pictures (no painted shadow). */
const TILE_TILES = ['dr', 'p5', 'b7'];
/** Retina tiles (owner 2026-10-17: "grafiki się blurują na retina"): the 1x previews are hand-cut art with
 *  no HD version, so on a 2x screen the tile shows the real table background instead, drawn about one image
 *  pixel per screen pixel (windows.css `.select_theme::before`); this is the part of it worth showing. */
const TILE_HD_POS: Record<string, string> = {
  classic_old: 'center top', // the lamps
  strips_blue: 'center top', // the lamps
  classic_saloon: 'center 35%',
  modern: 'center 45%', // the embossed spade
};
const ADVANCED_CARDS = ['2c', '3h', '4s', '5d', '6c', '7h', '8s', '9d', '10c', 'Jh', 'Qs', 'Kd', 'Ac'];
/** The Advanced tab's row of Mahjong tiles (one of each kind, the flowers and the back), stepped by its own arrows. */
const ADVANCED_TILES = ['m1', 'p5', 'b7', 'we', 'dr', 'dw', 'f1', 's2'];
/** Kulki's balls (owner 2026-11-03: "daj podgląd w wyglądzie"): three on each theme tile, all seven on the Advanced tab. */
const TILE_BALLS = [1, 3, 5];
const ADVANCED_BALLS = [1, 2, 3, 4, 5, 6, 7];

/** The deck a theme gives: on a phone at most 720 px wide the big-sign variant (`_v2`), as at the first start
 *  (options/storage.ts defaults; player report 2026-10-26: "Classic red" on a phone gave `classic`, whose signs
 *  could not be read in 10 columns). The footer's Cards button still steps to the other variants. */
export function themeDeck(deck: string): string {
  return document.body.id === 'mobile' && innerWidth <= 720 && /^(classic_old|modern|classic|strips)$/.test(deck) ? `${deck}_v2` : deck;
}

function applyTheme(theme: Theme, skin: SkinValues) {
  const same = skin.themeType === theme.id;
  const patch: Partial<SkinValues> = { themeType: theme.id, backgroundType: theme.background };
  // Re-picking the current theme keeps a customised deck/back (THEME-005).
  if (!same) {
    patch.deckType = themeDeck(theme.deck);
    patch.cardBackType = theme.back;
    patch.tileType = undefined; // the tiles follow the deck again
    patch.ballType = undefined; // and the balls
  }
  patch.themeAuthor = ''; // the key stays in the cookie (legacy readers), always empty since 2026-10-31
  uiStore().setSkin(patch);
}

function Tile({ theme, skin }: { theme: Theme; skin: SkinValues }) {
  const selected = skin.themeType === theme.id;
  const deck = selected ? skin.deckType : themeDeck(theme.deck);
  const back = selected ? skin.cardBackType : theme.back;
  const tiles = selected ? tileDeckOf(skin.deckType, skin.tileType) : tileDeckOf(theme.deck);
  const balls = selected ? ballSetOf(skin.deckType, skin.ballType) : ballSetOf(theme.deck);
  const modified = selected && (skin.deckType !== themeDeck(theme.deck) || skin.cardBackType !== theme.back || !!skin.tileType || !!skin.ballType);
  return (
    <div
      className={`skin_item select_theme${selected ? ' selected' : ''}`}
      style={
        {
          backgroundImage: `url("${previewSrc(theme.background)}")`,
          '--tile-hd': `url("${backgroundSrc(theme.background)}")`,
          '--tile-pos': TILE_HD_POS[theme.background] ?? 'center',
        } as CSSProperties
      }
      onClick={() => applyTheme(theme, skin)}
    >
      {TILE_CARDS.map((id) => (
        <img key={id} src={faceSrc(deck, id).replace(/\?v=\d+$/, '')} srcSet={faceSrcSet(deck, id)} className="card" alt="" />
      ))}
      <img src={backSrc(back).replace(/\?v=\d+$/, '')} srcSet={backSrcSet(back)} className="back_card" alt="" />
      {TILE_TILES.map((id) => (
        <img key={id} src={tileSrc(tiles, id, false, true)} srcSet={tileSrcSet(tiles, id, true)} className="tile" alt="" />
      ))}
      <div className="theme_balls">
        {TILE_BALLS.map((c) => (
          <img key={c} src={ballSrc(balls, c)} className="ball" alt="" />
        ))}
      </div>
      <div className="label_bg">
        {/* each text in its own element: a translated page (Chrome swaps every text for its own <font>) kept
            React from removing " (selected)" from the tile picked before, and the whole page went blank (report
            2026-10-26) */}
        <span>{theme.name}</span>
        {selected && <span>{t('optionWindowSelectedSkinItem')}</span>}
        <a
          href="#"
          className="skin_item_default"
          style={{ display: modified && document.body.id !== 'mobile' ? 'block' : 'none' }} // no "restore theme" link on phones (legacy)
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            uiStore().setSkin({ deckType: themeDeck(theme.deck), cardBackType: theme.back, tileType: undefined, ballType: undefined });
          }}
        >
          {t('optionWindowChangedThemeSkinItem')}
        </a>
      </div>
    </div>
  );
}

function Advanced({ skin }: { skin: SkinValues }) {
  const deckIndex = DECKS.indexOf(skin.deckType.replace(/_v[12]$/, ''));
  const backIndex = BACKS.indexOf(skin.cardBackType);
  const tiles = tileDeckOf(skin.deckType, skin.tileType);
  const tileIndex = TILE_SET_NAMES.indexOf(tileSetOf(skin.deckType, skin.tileType));
  const balls = ballSetOf(skin.deckType, skin.ballType);
  const ballIndex = BALL_SET_NAMES.indexOf(balls);
  const step = (list: string[], index: number, delta: number, key: 'deckType' | 'cardBackType' | 'tileType' | 'ballType') => {
    const next = index + delta;
    if (next < 0 || next >= list.length) return;
    uiStore().setSkin({ [key]: list[next] });
  };
  const navi = (id: string, cls: string, disabled: boolean, label: string, onClick: () => void) => (
    <a
      id={id}
      href="#"
      className={`${disabled ? 'no_more_item ' : ''}${cls} theme_item_navi`}
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      {label}
    </a>
  );
  return (
    <div className="skin_item select_advanced_theme" style={{ backgroundImage: `url("${backgroundSrc(skin.backgroundType)}")` }}>
      <div className="theme_board_top">
        <div className="cards_content">
          {ADVANCED_CARDS.map((id, i) => (
            <img key={id} src={faceSrc(skin.deckType, id).replace(/\?v=\d+$/, '')} srcSet={faceSrcSet(skin.deckType, id)} className="card" alt="" style={{ left: 515 - 36 * (ADVANCED_CARDS.length - i) } as CSSProperties} />
          ))}
          <img src={backSrc(skin.cardBackType).replace(/\?v=\d+$/, '')} srcSet={backSrcSet(skin.cardBackType)} className="back_card" alt="" />
        </div>
      </div>
      <div className="theme_board_bottom">
        <div className="theme_board_bottom_left">
          {navi('theme_n_c_l', 'theme_item_card', deckIndex <= 0, '<', () => step(DECKS, deckIndex, -1, 'deckType'))}
          {navi('theme_n_c_r', 'theme_item_card', deckIndex >= DECKS.length - 1, '>', () => step(DECKS, deckIndex, 1, 'deckType'))}
        </div>
        <div className="theme_board_bottom_right">
          {navi('theme_n_b_l', 'theme_item_back_card', backIndex <= 0, '<', () => step(BACKS, backIndex, -1, 'cardBackType'))}
          {navi('theme_n_b_r', 'theme_item_back_card', backIndex >= BACKS.length - 1, '>', () => step(BACKS, backIndex, 1, 'cardBackType'))}
        </div>
      </div>
      {/* Mahjong's tiles (owner 2026-10-31): their own set, stepped apart from the cards; a theme pick puts them back with its deck */}
      {/* its arrows either side of the row (owner 2026-11-03: with the balls' row the tab got a scroll bar on a PC) */}
      <div className="theme_board_tiles">
        {navi('theme_n_t_l', 'theme_item_tile', tileIndex <= 0, '<', () => step(TILE_SET_NAMES, tileIndex, -1, 'tileType'))}
        <div className="tiles_content">
          {ADVANCED_TILES.map((id) => (
            <img key={id} src={tileSrc(tiles, id, false, true)} srcSet={tileSrcSet(tiles, id, true)} className="tile" alt="" />
          ))}
          <img src={tileSrc(tiles, 'back', false, true)} srcSet={tileSrcSet(tiles, 'back', true)} className="tile tile_back" alt="" />
        </div>
        {navi('theme_n_t_r', 'theme_item_tile', tileIndex >= TILE_SET_NAMES.length - 1, '>', () => step(TILE_SET_NAMES, tileIndex, 1, 'tileType'))}
      </div>
      {/* Kulki's balls (owner 2026-11-03): their own set, stepped apart from the cards and the tiles; a theme pick puts them
          back with its deck */}
      <div className="theme_board_balls">
        {navi('theme_n_k_l', 'theme_item_ball', ballIndex <= 0, '<', () => step(BALL_SET_NAMES, ballIndex, -1, 'ballType'))}
        <div className="balls_content">
          {ADVANCED_BALLS.map((c) => (
            <img key={c} src={ballSrc(balls, c)} className="ball" alt="" />
          ))}
        </div>
        {navi('theme_n_k_r', 'theme_item_ball', ballIndex >= BALL_SET_NAMES.length - 1, '>', () => step(BALL_SET_NAMES, ballIndex, 1, 'ballType'))}
      </div>
    </div>
  );
}

export function ThemesWindow({ instance }: { instance: WindowInstance }) {
  const ui = useUiState();
  const skin = ui.options.skin;
  const [wide, setWide] = useState(() => innerWidth >= 1024);
  useEffect(() => {
    const mq = matchMedia('(min-width: 1024px)');
    const on = () => setWide(mq.matches);
    return onMediaChange(mq, on);
  }, []);
  // Opening the window brings the selected theme into view (owner 2026-10-17, like the Games window):
  // only the tile list scrolls, the tile centred where the list has room; only on open, so a tap on
  // another tile never moves the list under the finger.
  const listRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const tile = listRef.current?.querySelector<HTMLElement>('.select_theme.selected');
    if (!tile) return;
    let scroller: HTMLElement | null = tile.parentElement;
    while (scroller && !(scroller.scrollHeight > scroller.clientHeight && /auto|scroll/.test(getComputedStyle(scroller).overflowY))) scroller = scroller.parentElement;
    if (!scroller) return;
    const e = tile.getBoundingClientRect();
    const sc = scroller.getBoundingClientRect();
    scroller.scrollTop += e.top - sc.top - Math.max(0, (sc.height - e.height) / 2);
  }, []);
  const tiles = (
    <div ref={listRef}>
      {THEMES.map((theme) => (
        <Tile key={theme.id} theme={theme} skin={skin} />
      ))}
    </div>
  );
  return (
    <WindowFrame name="opt_skin" header={t('optionSkinHeader')} closing={instance.closing}>
      <div className="skin_content">
        {wide ? (
          <Tabs labels={[t('optionSkinTabThemeHeader'), t('optionSkinTabCardHeader')]}>
            {[tiles, <Advanced key="adv" skin={skin} />]}
          </Tabs>
        ) : (
          <Tabs labels={[t('optionSkinTabThemeHeader')]}>{[tiles]}</Tabs>
        )}
      </div>
    </WindowFrame>
  );
}
